import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { X, Camera, Loader2, Facebook, Instagram, Music2, Building2, AtSign, Bell, RotateCcw } from "lucide-react";

function avatarUrl(fileId) {
  if (!fileId) return null;
  return `${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${fileId}`;
}

export default function ProfileDialog({ open, onClose }) {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const push = usePushNotifications();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "", username: "", bio: "", social_fb: "", social_ig: "", social_tiktok: "",
    business_name: "", business_url: "", avatar_file_id: null,
    notif_earthquakes: true, notif_storms: true, notif_admin: true, notif_chat: false, notif_daily_surf: true,
  });

  useEffect(() => {
    if (!open || !user) return;
    queueMicrotask(() => setForm({
      name: user.name || "",
      username: user.username || "",
      bio: user.bio || "",
      social_fb: user.social_fb || "",
      social_ig: user.social_ig || "",
      social_tiktok: user.social_tiktok || "",
      business_name: user.business_name || "",
      business_url: user.business_url || "",
      avatar_file_id: user.avatar_file_id || null,
      notif_earthquakes: user.notif_earthquakes !== false,
      notif_storms: user.notif_storms !== false,
      notif_admin: user.notif_admin !== false,
      notif_chat: user.notif_chat === true,
      notif_daily_surf: user.notif_daily_surf !== false,
    }));
  }, [open, user]);

  if (!open) return null;

  const setField = (k) => (e) => {
    const v = e.target.value;
    queueMicrotask(() => setForm((f) => ({ ...f, [k]: v })));
  };

  const pickFile = () => fileRef.current?.click();

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return; }
    queueMicrotask(() => setUploading(true));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      queueMicrotask(() => setForm((f) => ({ ...f, avatar_file_id: data.avatar_file_id })));
      refresh?.();
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      queueMicrotask(() => setUploading(false));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    queueMicrotask(() => { setBusy(true); setErr(""); });
    try {
      const payload = { ...form };
      // remove empty username (let user keep existing one if blank)
      if (!payload.username) delete payload.username;
      const { data } = await http.patch("/auth/me", payload);
      refresh?.();
      toast.success("Profile saved");
      onClose?.();
    } catch (e2) {
      queueMicrotask(() => setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not save"));
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  };

  const av = avatarUrl(form.avatar_file_id);

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="profile-dialog"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl shadow-2xl w-full sm:max-w-lg my-auto flex flex-col"
        style={{ maxHeight: "min(92vh, 760px)" }}
      >
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--ink-2)]/10 shrink-0">
          <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>{t("profile_title")}</h3>
          <button type="button" onClick={onClose} className="text-[color:var(--ink-2)] hover:text-[color:var(--ink)]" data-testid="close-profile">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={pickFile}
              data-testid="profile-avatar-btn"
              className="w-20 h-20 rounded-full bg-[color:var(--sand-2)] overflow-hidden flex items-center justify-center text-[color:var(--ink-2)] border-2 border-dashed border-[color:var(--ink-2)]/30 hover:border-[color:var(--coral)] transition shrink-0"
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : av ? (
                <img src={av} alt="avatar" className="w-full h-full object-cover rounded-full" />
              ) : (
                <Camera className="w-6 h-6" />
              )}
            </button>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[color:var(--ink)]">{t("profile_photo")}</p>
              <p className="text-xs text-[color:var(--ink-2)]">{t("profile_photo_hint")}</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="profile-avatar-input"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1.5">{t("profile_name")}</label>
              <Input value={form.name} onChange={setField("name")} data-testid="profile-name" maxLength={60} className="rounded-2xl" />
            </div>
            <div>
              <label className="eyebrow block mb-1.5 flex items-center gap-1"><AtSign className="w-3 h-3" />{t("profile_username")}</label>
              <Input value={form.username} onChange={setField("username")} data-testid="profile-username" maxLength={24} pattern="^[a-zA-Z0-9_]+$" className="rounded-2xl" />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-1.5">{t("profile_bio")}</label>
            <Textarea value={form.bio} onChange={setField("bio")} data-testid="profile-bio" rows={2} maxLength={280} placeholder={t("profile_bio_ph")} className="rounded-2xl" />
            <p className="text-[10px] text-[color:var(--ink-2)] mt-1 text-right">{form.bio.length}/280</p>
          </div>

          <div>
            <p className="eyebrow mb-2">{t("profile_social")}</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-[#1877F2] shrink-0" />
                <Input value={form.social_fb} onChange={setField("social_fb")} data-testid="profile-fb" placeholder="https://facebook.com/your.handle" className="rounded-2xl" />
              </div>
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-[#E1306C] shrink-0" />
                <Input value={form.social_ig} onChange={setField("social_ig")} data-testid="profile-ig" placeholder="https://instagram.com/your.handle" className="rounded-2xl" />
              </div>
              <div className="flex items-center gap-2">
                <Music2 className="w-4 h-4 text-[color:var(--ink)] shrink-0" />
                <Input value={form.social_tiktok} onChange={setField("social_tiktok")} data-testid="profile-tiktok" placeholder="https://tiktok.com/@your.handle" className="rounded-2xl" />
              </div>
            </div>
          </div>

          <div>
            <p className="eyebrow mb-2 flex items-center gap-1"><Building2 className="w-3 h-3" />{t("profile_business")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={form.business_name} onChange={setField("business_name")} data-testid="profile-business-name" placeholder={t("profile_business_name")} maxLength={80} className="rounded-2xl" />
              <Input value={form.business_url} onChange={setField("business_url")} data-testid="profile-business-url" placeholder="https://yourbusiness.com" maxLength={200} className="rounded-2xl" />
            </div>
          </div>

          {err && <p className="text-sm text-[color:var(--emergency)]" data-testid="profile-error">{err}</p>}
        </div>

        {/* Notification preferences */}
        <div className="px-5 pb-5 space-y-3 border-t border-[color:var(--ink-2)]/10 pt-5">
          <p className="eyebrow flex items-center gap-1.5"><Bell className="w-3 h-3" />{t("notif_title")}</p>
          <p className="text-xs text-[color:var(--ink-2)]">{t("notif_hint")}</p>
          {[
            { k: "notif_earthquakes", label: t("notif_earthquakes") },
            { k: "notif_storms", label: t("notif_storms") },
            { k: "notif_daily_surf", label: t("notif_daily_surf") },
            { k: "notif_admin", label: t("notif_admin") },
            { k: "notif_chat", label: t("notif_chat") },
          ].map(({ k, label }) => (
            <label key={k} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
              <span className="text-sm text-[color:var(--ink)]">{label}</span>
              <input
                type="checkbox"
                data-testid={`toggle-${k}`}
                checked={!!form[k]}
                onChange={(e) => { const v = e.target.checked; queueMicrotask(() => setForm((f) => ({ ...f, [k]: v }))); }}
                className="w-5 h-5 accent-[color:var(--coral)] cursor-pointer"
              />
            </label>
          ))}

          {/* Reset push subscription */}
          <div className="pt-2 mt-2 border-t border-[color:var(--ink-2)]/10">
            <Button
              type="button"
              variant="outline"
              disabled={push.busy || !push.supported}
              onClick={async () => {
                await push.reset();
                if (push.hint) toast(push.hint);
                else toast.success("Notifications reset");
              }}
              data-testid="reset-notifications-btn"
              className="pill w-full border-[color:var(--navy)]/40 text-[color:var(--navy)] hover:bg-[color:var(--navy)] hover:text-white"
            >
              {push.busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
              Reset notifications
            </Button>
            <p className="text-[11px] text-[color:var(--ink-2)] mt-1.5 leading-snug">
              Re-subscribes your device for alerts. Use this if push has stopped arriving or you switched browsers.
            </p>
          </div>
        </div>

        <div className="p-5 border-t border-[color:var(--ink-2)]/10 flex justify-end gap-2 shrink-0 bg-white rounded-b-3xl">
          <Button type="button" variant="outline" onClick={onClose} className="pill">{t("cancel")}</Button>
          <Button type="submit" disabled={busy} data-testid="profile-save" className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {t("profile_save")}
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}

export { avatarUrl };
